import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/App";
import { Layout } from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Trophy, 
  CheckCircle2, 
  Clock,
  ChevronRight,
  Award
} from "lucide-react";

export default function ProgressPage() {
  const navigate = useNavigate();
  const [sops, setSops] = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [sopsRes, progressRes] = await Promise.all([
        apiClient.get("/sops"),
        apiClient.get("/progress"),
      ]);
      setSops(sopsRes.data);
      setProgress(progressRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getProgressForSop = (sopId) => {
    return progress.find((p) => p.sop_id === sopId);
  };

  const completedSops = sops.filter((sop) => getProgressForSop(sop.id)?.completed);
  const inProgressSops = sops.filter((sop) => {
    const p = getProgressForSop(sop.id);
    return p && !p.completed && p.completed_steps?.length > 0;
  });
  const notStartedSops = sops.filter((sop) => {
    const p = getProgressForSop(sop.id);
    return !p || (!p.completed && (!p.completed_steps || p.completed_steps.length === 0));
  });

  const overallProgress = sops.length > 0 
    ? Math.round((completedSops.length / sops.length) * 100) 
    : 0;

  const averageQuizScore = progress.filter((p) => p.quiz_score !== null).length > 0
    ? Math.round(
        progress.filter((p) => p.quiz_score !== null).reduce((sum, p) => sum + p.quiz_score, 0) /
        progress.filter((p) => p.quiz_score !== null).length
      )
    : null;

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6 max-w-4xl mx-auto">
          <div className="skeleton h-32 w-full"></div>
          <div className="skeleton h-64 w-full"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in-up max-w-4xl mx-auto">
        {/* Header */}
        <div>
          <h1 className="text-2xl lg:text-3xl text-[var(--rd-green)]" style={{ fontFamily: "'Cinzel', serif" }}>
            My Progress
          </h1>
          <p className="text-[hsl(var(--muted-foreground))] mt-2">
            Track your training journey and certifications.
          </p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="stat-card" data-testid="progress-stat-overall">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold tracking-wider uppercase text-[hsl(var(--muted-foreground))]">
                  Overall Progress
                </p>
                <p className="text-4xl font-semibold text-[var(--rd-green)] mt-2" style={{ fontFamily: "'Cinzel', serif" }}>
                  {overallProgress}%
                </p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-[var(--rd-green)]/10 flex items-center justify-center">
                <Trophy className="w-6 h-6 text-[var(--rd-green)]" />
              </div>
            </div>
            <div className="mt-4">
              <div className="rd-progress-track">
                <div className="rd-progress-fill" style={{ width: `${overallProgress}%` }} />
              </div>
            </div>
          </div>

          <div className="stat-card" data-testid="progress-stat-completed">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold tracking-wider uppercase text-[hsl(var(--muted-foreground))]">
                  Certifications
                </p>
                <p className="text-4xl font-semibold text-[var(--rd-green)] mt-2" style={{ fontFamily: "'Cinzel', serif" }}>
                  {completedSops.length}/{sops.length}
                </p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-[var(--rd-gold)]/10 flex items-center justify-center">
                <Award className="w-6 h-6 text-[var(--rd-gold)]" />
              </div>
            </div>
          </div>

          <div className="stat-card" data-testid="progress-stat-avg-score">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold tracking-wider uppercase text-[hsl(var(--muted-foreground))]">
                  Avg Assessment Score
                </p>
                <p className="text-4xl font-semibold text-[var(--rd-green)] mt-2" style={{ fontFamily: "'Cinzel', serif" }}>
                  {averageQuizScore !== null ? `${averageQuizScore}%` : "-"}
                </p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-blue-50 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Completed SOPs */}
        {completedSops.length > 0 && (
          <div>
            <div className="section-header">
              <h2 className="text-lg text-[var(--rd-green)] flex items-center gap-2" style={{ fontFamily: "'Cinzel', serif" }}>
                <CheckCircle2 className="w-5 h-5 text-[var(--rd-gold)]" />
                Completed & Certified
              </h2>
              <div className="section-header-line"></div>
            </div>
            <div className="space-y-3">
              {completedSops.map((sop) => {
                const sopProgress = getProgressForSop(sop.id);
                return (
                  <Card 
                    key={sop.id} 
                    className="border-[hsl(var(--border))] hover:shadow-md cursor-pointer transition-shadow"
                    onClick={() => navigate(`/sops/${sop.id}`)}
                    data-testid={`completed-sop-${sop.id}`}
                  >
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className="w-14 h-14 rounded-sm overflow-hidden flex-shrink-0">
                        <img
                          src={sop.image_url || "https://images.pexels.com/photos/1325706/pexels-photo-1325706.jpeg"}
                          alt={sop.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-[var(--rd-green)] truncate">{sop.title}</h3>
                        <p className="text-sm text-[hsl(var(--muted-foreground))]">
                          Score: {sopProgress?.quiz_score}%
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="badge-gold">Certified</span>
                        <ChevronRight className="w-5 h-5 text-[hsl(var(--muted-foreground))]" />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* In Progress SOPs */}
        {inProgressSops.length > 0 && (
          <div>
            <div className="section-header">
              <h2 className="text-lg text-[var(--rd-green)] flex items-center gap-2" style={{ fontFamily: "'Cinzel', serif" }}>
                <Clock className="w-5 h-5 text-blue-600" />
                In Progress
              </h2>
              <div className="section-header-line"></div>
            </div>
            <div className="space-y-3">
              {inProgressSops.map((sop) => {
                const sopProgress = getProgressForSop(sop.id);
                const stepsCompleted = sopProgress?.completed_steps?.length || 0;
                const totalSteps = sop.steps?.length || 0;
                const progressPercent = totalSteps > 0 ? Math.round((stepsCompleted / totalSteps) * 100) : 0;

                return (
                  <Card 
                    key={sop.id} 
                    className="border-[hsl(var(--border))] hover:shadow-md cursor-pointer transition-shadow"
                    onClick={() => navigate(`/sops/${sop.id}`)}
                    data-testid={`in-progress-sop-${sop.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4 mb-3">
                        <div className="w-14 h-14 rounded-sm overflow-hidden flex-shrink-0">
                          <img
                            src={sop.image_url || "https://images.pexels.com/photos/1325706/pexels-photo-1325706.jpeg"}
                            alt={sop.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-[var(--rd-green)] truncate">{sop.title}</h3>
                          <p className="text-sm text-[hsl(var(--muted-foreground))]">
                            {stepsCompleted} of {totalSteps} steps completed
                          </p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-[hsl(var(--muted-foreground))]" />
                      </div>
                      <div className="rd-progress-track">
                        <div className="rd-progress-fill" style={{ width: `${progressPercent}%` }} />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Not Started SOPs */}
        {notStartedSops.length > 0 && (
          <div>
            <div className="section-header">
              <h2 className="text-lg text-[var(--rd-green)]" style={{ fontFamily: "'Cinzel', serif" }}>
                Not Started
              </h2>
              <div className="section-header-line"></div>
            </div>
            <div className="space-y-3">
              {notStartedSops.map((sop) => (
                <Card 
                  key={sop.id} 
                  className="border-[hsl(var(--border))] hover:shadow-md cursor-pointer transition-shadow opacity-70 hover:opacity-100"
                  onClick={() => navigate(`/sops/${sop.id}`)}
                  data-testid={`not-started-sop-${sop.id}`}
                >
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-14 h-14 rounded-sm overflow-hidden flex-shrink-0 grayscale">
                      <img
                        src={sop.image_url || "https://images.pexels.com/photos/1325706/pexels-photo-1325706.jpeg"}
                        alt={sop.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-[var(--rd-green)] truncate">{sop.title}</h3>
                      <p className="text-sm text-[hsl(var(--muted-foreground))]">
                        {sop.steps?.length || 0} steps • {sop.quiz_questions?.length || 0} questions
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-[hsl(var(--muted-foreground))]" />
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
